import React from 'react';
import {
    Box, Button, TextField, Typography, IconButton,
    Modal, Fade,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import { useTheme } from "@mui/material";
import { tokens } from "../../../theme";
import Axios from 'axios';
import { getAuthHeader } from '../../../utils/authToken';

const TemplateManagementModal = ({
    open,
    onClose,
    setSuccessAlertOpen,
    setErrorAlertOpen,
    setSuccessAlertMessage,
    setErrorAlertMessage,
}) => {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);

    const [templates, setTemplates] = React.useState([]);
    const [editing, setEditing] = React.useState(null); // template object being edited
    const [isNew, setIsNew] = React.useState(false);

    const emptyTemplate = {
        name: "",
        category: "announcement",
        title: "",
        author: "",
        content: "",
        is_active: true,
    };

    const fetchTemplates = async () => {
        try {
            const res = await Axios.get(`${process.env.REACT_APP_URL}/api/v1/classroom-templates?category=announcement`, {
                headers: { Authorization: getAuthHeader() },
            });
            setTemplates(res.data);
        } catch (err) {
            setErrorAlertMessage("Failed to load templates");
            setErrorAlertOpen(true);
        }
    };

    React.useEffect(() => {
        if (open) {
            fetchTemplates();
            setEditing(null);
            setIsNew(false);
        }
    }, [open]);

    const handleSave = async () => {
        if (!editing.name || !editing.category || !editing.title || !editing.content) {
            setErrorAlertMessage("Please fill in all required fields (name, category, title, content).");
            setErrorAlertOpen(true);
            return;
        }

        try {
            if (isNew) {
                await Axios.post(`${process.env.REACT_APP_URL}/api/v1/classroom-templates`, editing, {
                    headers: { Authorization: getAuthHeader() },
                });
                setSuccessAlertMessage("Template created!");
            } else {
                await Axios.put(`${process.env.REACT_APP_URL}/api/v1/classroom-templates/${editing.id}`, editing, {
                    headers: { Authorization: getAuthHeader() },
                });
                setSuccessAlertMessage("Template updated!");
            }
            setSuccessAlertOpen(true);
            setEditing(null);
            setIsNew(false);
            fetchTemplates();
        } catch (err) {
            setErrorAlertMessage(err.response?.data?.error || "Failed to save template");
            setErrorAlertOpen(true);
        }
    };

    const handleDelete = async (id) => {
        try {
            await Axios.delete(`${process.env.REACT_APP_URL}/api/v1/classroom-templates/${id}`, {
                headers: { Authorization: getAuthHeader() },
            });
            setSuccessAlertMessage("Template deleted!");
            setSuccessAlertOpen(true);
            fetchTemplates();
            if (editing && editing.id === id) {
                setEditing(null);
            }
        } catch (err) {
            setErrorAlertMessage(err.response?.data?.error || "Failed to delete template");
            setErrorAlertOpen(true);
        }
    };

    const style = {
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: 900,
        maxHeight: '85vh',
        overflowY: 'auto',
        bgcolor: colors.primary[600],
        border: '2px solid grey',
        boxShadow: 24,
        p: 4,
    };

    return (
        <Modal open={open} onClose={onClose}>
            <Fade in={open}>
                <Box sx={style}>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                        <Typography variant="h4">Manage Templates</Typography>
                        <IconButton onClick={onClose}><CloseIcon /></IconButton>
                    </Box>

                    {!editing ? (
                        // LIST VIEW
                        <>
                            <Button
                                variant="contained"
                                startIcon={<AddIcon />}
                                onClick={() => { setEditing({ ...emptyTemplate }); setIsNew(true); }}
                                sx={{ mb: 2, backgroundColor: colors.greenAccent[600] }}
                            >
                                Add New Template
                            </Button>
                            {templates.map((tmpl) => (
                                <Box
                                    key={tmpl.id}
                                    sx={{
                                        display: "flex",
                                        justifyContent: "space-between",
                                        alignItems: "center",
                                        p: 1.5,
                                        mb: 1,
                                        borderRadius: 1,
                                        bgcolor: colors.primary[750],
                                    }}
                                >
                                    <Box>
                                        <Typography variant="h6">{tmpl.name}</Typography>
                                        {!tmpl.is_active && (
                                            <Typography variant="body2" color="textSecondary">
                                                (inactive)
                                            </Typography>
                                        )}
                                    </Box>
                                    <Box>
                                        <IconButton onClick={() => { setEditing({ ...tmpl }); setIsNew(false); }}>
                                            <EditIcon />
                                        </IconButton>
                                        <IconButton color="error" onClick={() => handleDelete(tmpl.id)}>
                                            <DeleteIcon />
                                        </IconButton>
                                    </Box>
                                </Box>
                            ))}
                            {templates.length === 0 && (
                                <Typography color="textSecondary" sx={{ mt: 2 }}>
                                    No templates yet. Click "Add New Template" to create one.
                                </Typography>
                            )}
                        </>
                    ) : (
                        // EDIT VIEW
                        <Box display="flex" flexDirection="column" gap={2}>
                            <Button
                                variant="text"
                                onClick={() => { setEditing(null); setIsNew(false); }}
                                sx={{ alignSelf: "flex-start" }}
                            >
                                Back to list
                            </Button>
                            <TextField
                                label="Name"
                                value={editing.name}
                                onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                                fullWidth
                                variant="filled"
                            />
                            <TextField
                                label="Title"
                                value={editing.title}
                                onChange={(e) => setEditing({ ...editing, title: e.target.value })}
                                fullWidth
                                variant="filled"
                            />
                            <TextField
                                label="Author"
                                value={editing.author || ""}
                                onChange={(e) => setEditing({ ...editing, author: e.target.value })}
                                fullWidth
                                variant="filled"
                            />
                            <TextField
                                label="Content"
                                value={editing.content}
                                onChange={(e) => setEditing({ ...editing, content: e.target.value })}
                                fullWidth
                                multiline
                                rows={15}
                                variant="filled"
                            />
                            <Typography variant="caption" color="textSecondary">
                                {"Supported variables: {{semester}}, {{course_title}}, {{date}}, {{student_name}}"}
                            </Typography>
                            <Box display="flex" gap={2}>
                                <Button
                                    variant="contained"
                                    onClick={handleSave}
                                    sx={{ backgroundColor: colors.greenAccent[600] }}
                                >
                                    {isNew ? "Create" : "Save Changes"}
                                </Button>
                                <Button
                                    variant="outlined"
                                    onClick={() => { setEditing(null); setIsNew(false); }}
                                >
                                    Cancel
                                </Button>
                            </Box>
                        </Box>
                    )}
                </Box>
            </Fade>
        </Modal>
    );
};

export default TemplateManagementModal;
